import * as React from "react";
import { ButtonType, CustomButton, SortableTableHeader, Table, TooltipItem } from "soprasteria-ui-library";
import { formatDate } from "../../../../../helpers/DateHelper";
import { IRole } from "../../../../../models/IRole";
import { IUserRoleGroup, IUserRoleGroupListItem } from "../../../../../models/IUserRoleGroup";
import { useAuth } from "../../../../../App";
import { UserRoleGroupApi } from "../../../../../api/UserRoleGroupApi";
import { RoleGroupModal } from "../../../../RoleGroupModal/RoleGroupModal";

interface IProps {
    clinicId: number;
    roleGroups: IUserRoleGroupListItem[];
    canEditRoleGroups: boolean;
    onSuccess: (message: string) => void;
}

export const RoleGroupTable: React.FC<IProps> = ({ clinicId, roleGroups, canEditRoleGroups, onSuccess }) => {
    const auth = useAuth();
    const api = new UserRoleGroupApi(auth);
    const abortController = new AbortController();
    const [renderModal, setRenderModal] = React.useState<boolean>(false);
    const [userRoleGroup, setUserRoleGroup] = React.useState<IUserRoleGroup>();
    const [selectedUserId, setSelectedUserId] = React.useState<number>();
    const [selectedClinicId, setSelectedClinicId] = React.useState<number>();

    React.useEffect(() => {
        if (userRoleGroup) {
            document.getElementById("open-rolegroup-modal")?.click();
            setRenderModal(true);
        }

        return () => {
            abortController.abort();
        }
    }, [userRoleGroup]);

    const closeModal = () => {
        setRenderModal(false);
        setUserRoleGroup(undefined);
    }

    const onSubmit = (newUserRoleGroup: boolean) => {
        document.getElementById("close-rolegroup-modal")?.click();
        onSuccess(newUserRoleGroup ? "Rollgruppen har lagts till" : "Rollgruppen har sparats");
    }

    const renderRoles = (roles: IRole[]) => {
        return roles.length > 0 ?
            roles.map((role) => {
                const uniqueId = Math.floor(Math.random() * 100000);
                return (
                    <TooltipItem key={uniqueId} title={role.roleName} description={role.roleDescription} showDisc />
                )
            }) : "";
    }

    const handleRowClick = (userRoleGroupId: number) => {
        loadUserRoleGroup(userRoleGroupId);
    }

    const loadUserRoleGroup = (userRoleGroupId: number) => {
        api.getUserRoleGroupById(userRoleGroupId, (data) => {
            setUserRoleGroup(data);
            setSelectedUserId(data.userId);
        }, undefined, undefined);
    }

    const onCreateRoleGroupClick = () => {
        setSelectedClinicId(clinicId);
        setSelectedUserId(undefined);
        setRenderModal(true);
    }

    return (
        <>
            <section className="role-group-table">
                {
                    canEditRoleGroups ?
                        <div className="panel text-right">
                            <CustomButton buttonText="Lägg till rollgrupp" buttonType={ButtonType.create} data-toggle="modal" data-target="#create-rolegroup-modal" onClick={onCreateRoleGroupClick} />
                        </div>
                        : null
                }
                <Table hoverableRows>
                    <thead>
                        <tr>
                            <SortableTableHeader headerText="Användare (Person)" propertyToSortBy="UserFullName" />
                            <SortableTableHeader headerText="HSA-ID Användare" propertyToSortBy="UserHsaId" />
                            <SortableTableHeader headerText="IT-system" propertyToSortBy="ApplicationName" />
                            <th>Roller</th>
                            <SortableTableHeader headerText="Fr.o.m." propertyToSortBy="DateFrom" />
                            <SortableTableHeader headerText="T.o.m." propertyToSortBy="DateTo" />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            roleGroups && roleGroups.length > 0
                                ? roleGroups.map((roleGroup, key) => {
                                    return (
                                        <tr key={key} className="clickable-row" onClick={() => handleRowClick(roleGroup.userRoleGroupId)}>
                                            <td style={{ whiteSpace: "nowrap" }}>{roleGroup.userFullName}</td>
                                            <td>{roleGroup.userHsaId}</td>
                                            <td>{roleGroup.applicationName}</td>
                                            <td>{renderRoles(roleGroup.roles)}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(roleGroup.dateFrom)}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{roleGroup.dateTo ? formatDate(roleGroup.dateTo) : "Tillsvidare"}</td>
                                        </tr>
                                    )
                                })
                                : <tr><td colSpan={6}>Det finns inga rollgrupper för mottagningen</td></tr>
                        }
                    </tbody>
                </Table>
            </section>
            {
                renderModal && (selectedUserId || selectedClinicId) ?
                    <RoleGroupModal openedFromClinic={true} userRoleGroup={userRoleGroup} userId={selectedUserId} clinicId={clinicId} canEdit={canEditRoleGroups} closeModal={closeModal} onSubmit={onSubmit} />
                    : null
            }
        </>
    )
}